<template>
  <v-container>
    <v-row>
      <h2 class="pb-2 pt-2">Verbindungen</h2>
    </v-row>
    <v-row>
      <v-col cols="12" class="pt-0">
        <small class="text-medium-emphasis">
          Fahrplan von der Bahn, Bewertung aus unserer eigenen Historie: wie oft diese Verbindung
          in der Vergangenheit aufgegangen wäre.
        </small>
      </v-col>
    </v-row>

    <!-- The one reason a signed-out visitor sees this page at all: the drawer entry and the two
         search widgets keep offering it (marked with a lock) rather than hiding it, so the page
         has to say why it is shut. -->
    <v-alert v-if="!oidcIsAuthenticated" type="info" variant="tonal" class="mt-2">
      <div class="sign-in-gate">
        <span>Die Verbindungssuche ist neu und vorerst nur für angemeldete Nutzer freigeschaltet.</span>
        <v-btn size="small" variant="text" prepend-icon="mdi-login" @click="signIn()">
          Anmelden
        </v-btn>
      </div>
    </v-alert>

    <template v-else>
    <v-row dense>
      <v-col cols="12" md="3">
        <StationField v-model="from" :station="fromStation" label="Von" icon="mdi-map-marker"
                      @update:station="fromStation = $event" />
      </v-col>
      <v-col cols="12" md="3">
        <StationField v-model="to" :station="toStation" label="Nach" icon="mdi-map-marker-check"
                      @update:station="toStation = $event" />
      </v-col>
      <v-col cols="12" md="2">
        <DateTimeField v-model="datetime" label="Abfahrt" :min="minDatetime" />
      </v-col>
      <v-col cols="12" md="2">
        <v-select
          v-model="windowDays"
          label="Bewertung"
          prepend-inner-icon="mdi-calendar"
          :items="windowItems"
          item-title="title"
          item-value="days"
          hide-details="auto"
        />
      </v-col>
      <v-col cols="12" md="2" class="d-flex align-center">
        <v-btn color="primary" block :disabled="!canSearch" :loading="loading" @click="search">
          Suchen
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" class="py-0">
        <AnonymousNotice />
      </v-col>
    </v-row>

    <v-row v-if="ratingWindow">
      <v-col cols="12" class="pt-0">
        <small class="text-medium-emphasis">
          Bewertet über {{ ratingWindow.days }} {{ ratingWindow.days === 1 ? 'Tag' : 'Tage' }}
          ({{ toDate(ratingWindow.from) }} bis {{ toDate(ratingWindow.to) }}).
          <template v-if="cappedByAnonymousLimit">Mit Anmeldung wird das Fenster länger.</template>
        </small>
      </v-col>
    </v-row>

    <v-alert v-if="error" type="warning" variant="tonal" class="mt-4">{{ error }}</v-alert>

    <LoadingIndicator v-else-if="loading" />

    <template v-else-if="connections">
      <v-expansion-panels v-if="connections.length" v-model="openPanel" class="mt-4">
        <ConnectionRow v-for="connection in connections" :key="connection.id"
                       :connection="connection" />
      </v-expansion-panels>
      <v-alert v-else type="info" variant="tonal" class="mt-4">
        Für diesen Zeitpunkt findet die Bahn keine Verbindung. Weiter als etwa drei Monate im
        Voraus gibt es noch keinen Fahrplan.
      </v-alert>
    </template>
    </template>
  </v-container>
</template>

<script>
import { DateTime } from 'luxon'
import { mapGetters } from 'vuex'
import AnonymousNotice from '@/components/AnonymousNotice.vue'
import ConnectionRow from '@/components/ConnectionRow.vue'
import DateTimeField from '@/components/DateTimeField.vue'
import DateTimeMixin from '@/mixins/DateTimeMixin'
import LoadingIndicator from '@/components/LoadingIndicator.vue'
import SignInMixin from '@/mixins/SignInMixin'
import StationField from '@/components/StationField.vue'
import TitleMixin from '@/mixins/TitleMixin'

// The backend caps the rating window at 90 days anyway; asking for more only
// produces a `capped` flag we would then have to explain away.
const MAX_WINDOW_DAYS = 90;
const DEFAULT_WINDOW_DAYS = 30;

// Same three-step idiom as the drawer's "Zeitraum", minus the unbounded options this
// endpoint refuses. The anonymous cap is deliberately *not* mirrored as disabled entries
// here: it is applied server-side and reported back in ratingWindow.capped, which this page
// already prints, so a locked-looking default would only be a second, contradictable copy.
const WINDOW_OPTIONS = [
  { title: '7 Tage', days: 7 },
  { title: '30 Tage', days: DEFAULT_WINDOW_DAYS },
  { title: '90 Tage', days: MAX_WINDOW_DAYS },
];

// The datetime-local value travels to the backend as a naked stamp and is parsed there as
// Europe/Berlin, so every default and bound this page computes is Berlin wall time — not the
// browser's. West of Berlin, late in the evening, a browser-local default is already
// yesterday for the server, which answers 400.
const BERLIN = 'Europe/Berlin';
const STAMP_FORMAT = "yyyy-MM-dd'T'HH:mm";

function berlinNow() {
  return DateTime.now().setZone(BERLIN);
}

// The search opens on the next full hour, both on a fresh visit and when a link carries a
// stamp we cannot read.
function defaultDatetime() {
  return berlinNow().plus({ hours: 1 }).startOf('hour').toFormat(STAMP_FORMAT);
}

/**
 * `search.from`/`search.to` hold {evaId, name}. State written by an older build has no such
 * keys at all, and state from one intermediate version stored a bare eva id string — neither
 * may break the page, so both normalise to the same shape (falling back to showing the id,
 * which is what that version showed anyway, and which self-heals on the next pick).
 */
function normalizeStation(value) {
  if (!value) {
    return null;
  }
  if (typeof value === 'string') {
    return { evaId: value, name: value };
  }
  if (typeof value === 'object' && value.evaId) {
    return { evaId: value.evaId, name: value.name || value.evaId };
  }
  return null;
}

export default {
  name: 'Connections',

  components: { AnonymousNotice, ConnectionRow, DateTimeField, LoadingIndicator, StationField },

  mixins: [DateTimeMixin, SignInMixin, TitleMixin],

  data() {
    var fromStation = normalizeStation(this.$store.state.search.from);
    var toStation = normalizeStation(this.$store.state.search.to);
    return {
      from: fromStation ? fromStation.evaId : null,
      to: toStation ? toStation.evaId : null,
      fromStation: fromStation,
      toStation: toStation,
      datetime: defaultDatetime(),
      connections: null,
      // Not named `window`: that shadows the global of the same name.
      ratingWindow: null,
      error: null,
      loading: false,
      openPanel: null,
    };
  },

  computed: {
    // The gate. `/api/connections` and `/api/stations` are the only read endpoints that require a
    // token, so without one every field on this page would answer 401 — hiding the form is what
    // keeps that from looking like a broken search rather than a closed door.
    ...mapGetters('oidcStore', ['oidcIsAuthenticated']),

    title() {
      return `Verbindungen - ${import.meta.env.VITE_NAME}`;
    },

    canSearch() {
      return Boolean(this.from && this.to && this.datetime);
    },

    windowItems() {
      return WINDOW_OPTIONS;
    },

    // This page's own rating window, persisted alongside the station pair. Deliberately not
    // the drawer's `search.range`: that control means "how far back do the punctuality pages
    // look" and defaulted this page to 7 days, under which the backend's minimum sample size
    // renders most legs "nicht bewertbar" — a page of empty verdicts on first visit.
    windowDays: {
      get() {
        var days = this.$store.state.search.connectionDays;
        return WINDOW_OPTIONS.some(option => option.days === days) ? days : DEFAULT_WINDOW_DAYS;
      },
      set(value) {
        this.$store.commit('updateConnectionDays', value);
      },
    },

    // Berlin's current day, so a past date — which the backend answers 400 for — is not
    // reachable by scrolling the picker back one step.
    minDatetime() {
      return berlinNow().startOf('day').toFormat(STAMP_FORMAT);
    },

    // `capped` is also raised by the service-wide ceiling, where signing in changes nothing.
    // The anonymous cap is what bit only when there is one and the window came back no larger
    // than it.
    cappedByAnonymousLimit() {
      var cap = this.$store.state.anonymousHistoryDays;
      return Boolean(this.ratingWindow && this.ratingWindow.capped
        && cap != null && this.ratingWindow.days <= cap);
    },
  },

  watch: {
    // Clicking "Verbindungen" in the drawer while already here drops the query string but
    // reuses the component, which otherwise leaves the previous results on screen under an
    // address that no longer describes them.
    '$route.query': function(query) {
      if (this.$route.name !== 'connections') {
        return;
      }
      if (!query.from || !query.to) {
        this.connections = null;
        this.ratingWindow = null;
        this.error = null;
        this.openPanel = null;
        return;
      }
      if (query.from === this.from && query.to === this.to && query.datetime === this.datetime) {
        return;   // our own replace() after a search
      }
      this.applyQuery(query);
      this.search();
    },
  },

  mounted() {
    // A shared link carries the search in the query, and it wins over the store.
    var query = this.$route.query;
    if (query.from && query.to) {
      this.applyQuery(query);
      this.search();
    }
  },

  methods: {
    // The eva ids are what the API needs; the names ride along so a shared link opens with
    // readable stations instead of "8000105" in both fields.
    applyQuery: function(query) {
      this.from = query.from;
      this.to = query.to;
      this.fromStation = { evaId: query.from, name: query.fromName || query.from };
      this.toStation = { evaId: query.to, name: query.toName || query.to };
      // A shared link's stamp is whatever was in the address bar. The native input used to
      // swallow an unreadable one and show an empty field, which looks like the link is broken;
      // falling back to the default at least leaves a searchable page.
      if (query.datetime) {
        this.datetime = DateTime.fromFormat(query.datetime, STAMP_FORMAT).isValid
          ? query.datetime
          : defaultDatetime();
      }
    },

    search: function() {
      // The single choke point for every caller — the button, the mount hook for a shared link,
      // and the query watcher. Without the token the endpoint answers 401, so a signed-out visit
      // to a shared link would otherwise render an error where the sign-in prompt belongs.
      if (!this.oidcIsAuthenticated || !this.canSearch) {
        return;
      }
      this.loading = true;
      this.error = null;
      this.openPanel = null;
      this.$store.commit('updateSearchStations', { from: this.fromStation, to: this.toStation });

      // Keeps the result linkable. An unknown name drops out of the URL rather than repeating
      // the id. `catch` swallows the redundant-navigation rejection that vue-router raises
      // when nothing actually changed.
      this.$router.replace({
        name: 'connections',
        query: {
          from: this.from,
          to: this.to,
          fromName: this.fromStation ? this.fromStation.name : undefined,
          toName: this.toStation ? this.toStation.name : undefined,
          datetime: this.datetime,
        },
      }).catch(() => {});

      this.$api.get('/connections', {
        params: { from: this.from, to: this.to, datetime: this.datetime, days: this.windowDays },
      })
        .then(response => {
          this.connections = response.data.connections || [];
          this.ratingWindow = response.data.ratingWindow || null;
        })
        .catch(e => {
          this.connections = null;
          this.ratingWindow = null;
          this.error = this.errorMessage(e);
          this.$log.warn(`Connection search failed: ${e}`);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    // The backend hands out ready German text for the states it knows about — notably 429,
    // which is not an edge case here: TLS terminates upstream in this deployment, so every
    // visitor shares one search budget. Telling a rate-limited user to check their input
    // sends them to fix something that is not wrong.
    errorMessage: function(e) {
      var response = e.response;
      if (response && response.status === 503) {
        return 'Die Fahrplanauskunft ist gerade nicht erreichbar. Bitte später noch einmal versuchen.';
      }
      var supplied = response && response.data && response.data.message;
      if (typeof supplied === 'string' && supplied.length) {
        return supplied;
      }
      return 'Die Suche hat nicht geklappt. Bitte Eingaben prüfen.';
    },
  },
};
</script>

<style scoped>
/* Same layout as AnonymousNotice's alert, which offers signing in the same way. */
.sign-in-gate {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  align-items: center;
  justify-content: space-between;
}
</style>
